class DlPageProductListHeroCardM extends HTMLElement {
  connectedCallback() {
    this.variants = this.readVariants();
    this.formInput = this.querySelector('input[name="id"]');
    this.price = this.querySelector('[data-price]');
    this.compare = this.querySelector('[data-compare]');
    this.coupon = this.querySelector('[data-coupon-copy]');
    this.discountBadge = this.querySelector('[data-discount-badge]');
    this.hasCustomDiscountBadge = this.dataset.hasCustomDiscountBadge === 'true';
    this.submit = this.querySelector('.dl-product-form__submit-button-h');
    this.submitLabel = this.submit?.querySelector('span');
    this.currentVariant = null;
    this.bindEvents();
    this.updateVariant(this.formInput?.value);
  }

  readVariants() {
    try {
      return JSON.parse(this.querySelector('[data-variants-json]')?.textContent || '[]');
    } catch (error) {
      console.warn('[dl-page-product-list-hero-m] Invalid variants JSON', error);
      return [];
    }
  }

  bindEvents() {
    this.addEventListener('click', (event) => {
      const color = event.target.closest('[data-option-value]');
      if (color) {
        this.querySelectorAll(`[data-option-index="${color.dataset.optionIndex}"]`).forEach((item) => {
          const isSelected = item === color;
          item.classList.toggle('is-selected', isSelected);
          item.classList.toggle('dl-selected', isSelected);
        });
        this.updateFromOptions();
        return;
      }

      const accessoryToggle = event.target.closest('[data-accessory-toggle]');
      if (accessoryToggle) {
        const accessory = accessoryToggle.closest('[data-accessory]');
        const selected = !accessory.classList.contains('is-selected');
        accessory.classList.toggle('is-selected', selected);
        accessoryToggle.setAttribute('aria-pressed', String(selected));
        this.updateTotalPrice();
        return;
      }

      const couponButton = event.target.closest('[data-coupon-copy]');
      if (couponButton && couponButton.dataset.code) {
        this.copyCoupon(couponButton);
      }
    });

    this.querySelectorAll('.dl-page-product-list-hero-m__select').forEach((select) => {
      select.addEventListener('change', () => this.updateFromOptions());
    });

    this.querySelectorAll('.dl-page-product-list-hero-m__plan').forEach((select) => {
      select.addEventListener('change', () => this.updateAccessoryPrice(select));
    });
  }

  selectedOptions() {
    const options = [];
    this.querySelectorAll('.dl-page-product-list-hero-m__color.is-selected').forEach((color) => {
      options[Number(color.dataset.optionIndex)] = color.dataset.optionValue;
    });
    this.querySelectorAll('.dl-page-product-list-hero-m__select').forEach((select) => {
      options[Number(select.dataset.optionIndex)] = select.value;
    });
    return options;
  }

  updateFromOptions() {
    const selected = this.selectedOptions();
    const normalizeOption = (value) => String(value ?? '').trim();
    const variant = this.variants.find((item) => item.options.every((value, index) => {
      return selected[index] == null || normalizeOption(value) === normalizeOption(selected[index]);
    }));
    if (variant) this.updateVariant(variant.id);
  }

  updateVariant(variantId) {
    const variant = this.variants.find((item) => String(item.id) === String(variantId));
    if (!variant) return;
    this.currentVariant = variant;

    if (this.formInput) {
      this.formInput.value = variant.id;
      this.formInput.dataset.variantSku = variant.sku || '';
    }

    this.querySelectorAll('.dl-page-product-list-hero-m__image').forEach((image) => {
      image.classList.toggle('is-selected', String(image.dataset.variantId) === String(variant.id));
    });

    if (this.coupon) {
      const code = variant.couponCode || '';
      this.coupon.dataset.code = code;
      this.coupon.hidden = !code;
      const label = this.coupon.querySelector('span');
      if (label) label.textContent = `${code}`;
    }

    if (this.discountBadge && !this.hasCustomDiscountBadge) {
      const discountBadge = variant.discountBadge || '';
      this.discountBadge.textContent = discountBadge;
      this.discountBadge.hidden = !discountBadge;
    }

    if (this.submit) {
      this.submit.disabled = !variant.available;
      this.submit.toggleAttribute('preorder', variant.available && variant.isPreorder);
    }
    if (this.submitLabel) {
      if (!variant.available) {
        this.submitLabel.textContent = this.dataset.soldOutButtonLabel;
      } else if (variant.isPreorder) {
        this.submitLabel.textContent = this.dataset.preorderButtonLabel;
      } else {
        this.submitLabel.textContent = this.dataset.defaultButtonLabel;
      }
    }

    this.updateTotalPrice();
  }

  updateAccessoryPrice(select) {
    const accessory = select.closest('[data-accessory]');
    const option = select.selectedOptions[0];
    const basePrice = Number(accessory.dataset.variantPrice || 0);
    let finalPrice = basePrice;
    const adjustment = Number(option.dataset.adjustmentValue || 0);

    if (option.dataset.adjustmentType === 'percentage') {
      finalPrice = Math.round(basePrice * (100 - adjustment) / 100);
    } else if (option.dataset.adjustmentType === 'fixed_amount') {
      finalPrice = Math.max(0, basePrice - adjustment * 100);
    }

    const price = accessory.querySelector('[data-accessory-price]');
    const compare = accessory.querySelector('[data-accessory-compare]');
    accessory.dataset.currentPrice = String(finalPrice);
    if (price && typeof window.formatMoney === 'function') price.textContent = window.formatMoney(finalPrice);
    if (compare) compare.hidden = finalPrice === basePrice;
    this.updateTotalPrice();
  }

  updateTotalPrice() {
    if (!this.price || !this.currentVariant) return;

    const mainPrice = Number(this.currentVariant.displayPrice || 0);
    const mainComparePrice = Number(this.currentVariant.comparePrice || 0);
    let totalPrice = mainPrice;
    let totalComparePrice = mainComparePrice > mainPrice ? mainComparePrice : mainPrice;
    let showComparePrice = mainComparePrice > mainPrice;
    const accessory = this.querySelector('[data-accessory]');
    if (accessory?.classList.contains('is-selected')) {
      const accessoryPrice = Number(accessory.dataset.currentPrice || 0);
      const accessoryComparePrice = Number(accessory.dataset.variantPrice || 0);
      totalPrice += accessoryPrice;
      totalComparePrice += accessoryComparePrice;
      if (accessoryComparePrice > accessoryPrice) showComparePrice = true;
    }

    if (typeof window.formatMoney === 'function') {
      this.price.textContent = window.formatMoney(totalPrice);
      if (this.compare) this.compare.textContent = window.formatMoney(totalComparePrice);
    } else {
      this.price.textContent = this.currentVariant.priceFormatted;
      if (this.compare) this.compare.textContent = this.currentVariant.compareFormatted || '';
    }

    if (this.compare) this.compare.hidden = !showComparePrice;
  }

  async copyCoupon(button) {
    try {
      await navigator.clipboard.writeText(button.dataset.code);
      button.classList.add('is-copied');
      window.setTimeout(() => button.classList.remove('is-copied'), 1200);
    } catch (error) {
      console.warn('[dl-page-product-list-hero-m] Copy failed', error);
    }
  }
}

if (!customElements.get('dl-page-product-list-hero-card-m')) {
  customElements.define('dl-page-product-list-hero-card-m', DlPageProductListHeroCardM);
}
